import DownloadIcon from "@mui/icons-material/Download";
import { CircularProgress } from "@mui/material";
import { PaymentMethod } from "@stripe/stripe-js";
import cx from "classnames";
import React, { useCallback, useEffect, useState } from "react";

import billingStyles from "components/Payment/UpgradeFlowComponents/BillingTab.module.scss";
import ManagePaymentMethodsModal from "components/Payment/UpgradeFlowComponents/ManagePaymentMethodsModal";
import { InvoiceCompact } from "models/Payment";
import { Subscription } from "models/User";
import { getInvoices, getPaymentMethods } from "services/SubscriptionService";
import { formatDate, formatPrice, formatTimestamp } from "utils/helper";

import styles from "../ProfilePage.module.scss";

interface AutopilotBillingTabProps {
  subscription: Subscription;
  renewalDate: string | null;
  estimatedCost: { currency: string; unit_amount_decimal: number } | null;
}

const AutopilotBillingTab: React.FC<AutopilotBillingTabProps> = ({
  subscription,
  renewalDate,
  estimatedCost,
}) => {
  const [defaultMethod, setDefaultMethod] = useState<PaymentMethod | null>(
    null,
  );
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[] | null>(
    null,
  );
  const [invoices, setInvoices] = useState<InvoiceCompact[] | null>(null);
  const [showManageMethodsModal, setShowManageMethodsModal] = useState(false);

  const fetchPaymentMethods = useCallback(() => {
    setPaymentMethods(null);
    getPaymentMethods(subscription.id).then((data) => {
      setPaymentMethods(data.payment_methods);
      setDefaultMethod(data.default_payment_method || null);
    });
  }, [subscription.id]);

  const fetchInvoices = useCallback(() => {
    getInvoices(subscription.id).then((data: InvoiceCompact[]) => {
      // Only Autopilot API usage invoices belong on this page.
      setInvoices(data.filter((invoice) => !!invoice.metadata?.is_autopilot));
    });
  }, [subscription.id]);

  useEffect(() => {
    fetchPaymentMethods();
    fetchInvoices();
  }, [fetchPaymentMethods, fetchInvoices]);

  const defaultMethodLabel = (() => {
    if (!defaultMethod) return "--";
    if (defaultMethod.card) {
      return `**** **** **** ${defaultMethod.card.last4}`;
    }
    return defaultMethod.type;
  })();

  return (
    <div className={styles.billingTabRoot}>
      <div className={styles.developerUsageSection}>
        <div className={styles.developerUsageSummaryCards}>
          <div className={styles.developerUsageSummaryCard}>
            <span className={styles.developerUsageSummaryLabel}>
              Next renewal date
            </span>
            <span className={styles.developerUsageSummaryValue}>
              {renewalDate ? formatDate(renewalDate) : "--"}
            </span>
          </div>
          <div className={styles.developerUsageSummaryCard}>
            <span className={styles.developerUsageSummaryLabel}>
              Estimated cost
            </span>
            <span className={styles.developerUsageSummaryValue}>
              {estimatedCost ? formatPrice(estimatedCost) : "--"}
            </span>
          </div>
          <div className={styles.developerUsageSummaryCard}>
            <span className={styles.developerUsageSummaryLabel}>
              Payment method
            </span>
            {paymentMethods === null ? (
              <CircularProgress size={18} sx={{ color: "#bdb8ff" }} />
            ) : (
              <>
                <span className={styles.developerUsageSummaryValue}>
                  {defaultMethodLabel}
                </span>
                <div
                  className={billingStyles.manageMethods}
                  onClick={() => setShowManageMethodsModal(true)}
                >
                  Manage Payment methods
                </div>
              </>
            )}
          </div>
        </div>

        {/* Invoices */}
        <h5 style={{ marginTop: 24 }}>Invoices</h5>
        {invoices === null && (
          <div className={styles.developerCenter}>
            <CircularProgress size={28} sx={{ color: "#bdb8ff" }} />
          </div>
        )}

        {invoices && invoices.length > 0 && (
          <div className={styles.developerUsageTable}>
            <div
              className={cx(
                styles.developerUsageTableRow,
                styles.developerUsageTableHeader,
              )}
            >
              <div>Date</div>
              <div>Frames</div>
              <div>Amount</div>
              <div>Invoice</div>
            </div>
            {invoices.map((invoice) => (
              <div key={invoice.id} className={styles.developerUsageTableRow}>
                <div className={styles.developerUsageTableCell}>
                  {invoice.date
                    ? formatTimestamp(invoice.date, {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })
                    : "--"}
                </div>
                <div className={styles.developerUsageTableCell}>
                  {invoice.metadata?.frames_used ?? "--"}
                </div>
                <div className={styles.developerUsageTableCell}>
                  {formatPrice(invoice.price)}
                </div>
                <div className={styles.developerUsageTableCell}>
                  {invoice.receipt_url ? (
                    <span
                      className={billingStyles.downloadInvoice}
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        invoice.receipt_url &&
                        window.open(invoice.receipt_url, "_blank")
                      }
                    >
                      <DownloadIcon /> Download
                    </span>
                  ) : (
                    "--"
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {invoices && invoices.length === 0 && (
          <p className={styles.developerEmptyText}>No invoices yet.</p>
        )}
      </div>

      {showManageMethodsModal && (
        <ManagePaymentMethodsModal
          onBack={() => setShowManageMethodsModal(false)}
          paymentMethods={paymentMethods}
          defaultMethod={defaultMethod}
          fetchPaymentMethods={fetchPaymentMethods}
          subscription={subscription}
          embedded
        />
      )}
    </div>
  );
};

export default AutopilotBillingTab;
